import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import { Button } from "../../components/ui/button";
import api, { formatApiError } from "../../lib/api";
import { Loader2, RefreshCw, Bell, Users, Mail } from "lucide-react";

const ALERT_TYPES = ["mca.status_change", "mca.new_filing", "financial.shift", "document.new"];

export default function AdminMonitoring() {
  const [alerts, setAlerts] = useState([]);
  const [nudgeStats, setNudgeStats] = useState(null);
  const [nudgeHistory, setNudgeHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [digestStats, setDigestStats] = useState(null);
  const [digestRunning, setDigestRunning] = useState(false);
  const [digestHistory, setDigestHistory] = useState([]);

  // Manual alert form
  const [form, setForm] = useState({
    startup_id: "",
    alert_type: "document.new",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/admin/monitoring/alerts?limit=100").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/admin/mandate-nudges/history?limit=10").then((r) => setNudgeHistory(r.data)).catch(() => {});
    api.get("/admin/monitoring/digest/history?limit=10").then((r) => setDigestHistory(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const runNudges = async (force = false, dry = false) => {
    setRunning(true);
    try {
      const url = `/admin/mandate-nudges/run-now?${force ? "force=true&" : ""}${dry ? "dry_run=true" : ""}`;
      const { data } = await api.post(url);
      setNudgeStats(data);
      toast.success(`Nudged ${data.nudged} · Skipped low-events ${data.skipped_low_events}`);
      if (!dry) load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const submitAlert = async () => {
    if (!form.startup_id || !form.message) {
      toast.error("Startup ID and message are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/monitoring/alerts", form);
      toast.success(`Alert ${data.alert_id} fanned out to ${data.dispatched_to.length} investors`);
      setForm({ startup_id: "", alert_type: "document.new", message: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const runDigest = async (dry = false, force = false) => {
    setDigestRunning(true);
    try {
      const params = new URLSearchParams();
      if (dry) params.set("dry_run", "true");
      if (force) params.set("force", "true");
      const { data } = await api.post(`/admin/monitoring/digest/run-now?${params.toString()}`);
      setDigestStats(data);
      toast.success(`Digest — sent ${data.sent}, skipped ${data.skipped_empty} empty, ${data.skipped_already_sent} already-sent`);
      if (!dry) load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDigestRunning(false);
    }
  };

  return (
    <DashboardShell title="Monitoring & Nudges" subtitle="ADMIN · ALERTS + MANDATE NUDGES">
      <Toaster richColors position="top-right" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Manual alert */}
        <Card testId="manual-alert-card">
          <div className="overline mb-3 flex items-center gap-2">
            <Bell size={14} /> Manual Alert
          </div>
          <div className="space-y-2 text-sm">
            <input
              type="text"
              placeholder="startup_id (e.g. usr_startup_greengrid)"
              value={form.startup_id}
              onChange={(e) => setForm({ ...form, startup_id: e.target.value })}
              data-testid="manual-alert-startup-id"
              className="w-full border border-navy/20 rounded px-3 py-2 text-sm"
            />
            <select
              value={form.alert_type}
              onChange={(e) => setForm({ ...form, alert_type: e.target.value })}
              data-testid="manual-alert-type"
              className="w-full border border-navy/20 rounded px-3 py-2 text-sm"
            >
              {ALERT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <textarea
              placeholder="Alert message that will reach investors"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="manual-alert-message"
              rows={3}
              className="w-full border border-navy/20 rounded px-3 py-2 text-sm"
            />
            <Button onClick={submitAlert} disabled={submitting} data-testid="manual-alert-submit">
              {submitting ? <Loader2 className="animate-spin" size={14} /> : "Fire Alert"}
            </Button>
          </div>
        </Card>

        {/* Nudge controls */}
        <Card testId="nudge-controls-card">
          <div className="overline mb-3 flex items-center gap-2">
            <Users size={14} /> Mandate Nudges
          </div>
          <p className="text-xs text-ink-muted mb-3">
            Emails investors with drift ≥ 60% asking them to refresh preferences. Weekly cron (Mon
            05:00 UTC) already runs — use these buttons for ad-hoc dispatch.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => runNudges(false, true)}
              disabled={running}
              data-testid="nudge-dry-run"
            >
              {running ? <Loader2 className="animate-spin" size={14} /> : "Dry Run"}
            </Button>
            <Button
              size="sm"
              onClick={() => runNudges(false, false)}
              disabled={running}
              data-testid="nudge-run"
            >
              {running ? <Loader2 className="animate-spin" size={14} /> : <><RefreshCw size={12} className="mr-1" /> Run Now</>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => runNudges(true, false)}
              disabled={running}
              data-testid="nudge-force"
            >
              Force (skip cooldown)
            </Button>
          </div>
          {nudgeStats && (
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-testid="nudge-stats">
              <div className="border border-navy/[0.08] p-2 rounded">
                <div className="font-mono uppercase text-[10px] text-ink-muted">Evaluated</div>
                <div className="font-heading text-lg">{nudgeStats.evaluated}</div>
              </div>
              <div className="border border-navy/[0.08] p-2 rounded">
                <div className="font-mono uppercase text-[10px] text-ink-muted">Nudged</div>
                <div className="font-heading text-lg text-gold">{nudgeStats.nudged}</div>
              </div>
              <div className="border border-navy/[0.08] p-2 rounded">
                <div className="font-mono uppercase text-[10px] text-ink-muted">Below threshold</div>
                <div className="font-heading text-lg">{nudgeStats.skipped_below_threshold}</div>
              </div>
              <div className="border border-navy/[0.08] p-2 rounded">
                <div className="font-mono uppercase text-[10px] text-ink-muted">Low events</div>
                <div className="font-heading text-lg">{nudgeStats.skipped_low_events}</div>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card testId="alerts-history-card" className="mb-6">
        <div className="overline mb-3">Monitoring Alerts — most recent</div>
        {alerts.length === 0 ? (
          <div className="text-ink-muted italic text-sm">No alerts yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                <th className="text-left py-2">When</th>
                <th className="text-left py-2">Startup</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Message</th>
                <th className="text-right py-2 pr-2">Dispatched</th>
              </tr>
            </thead>
            <tbody>
              {alerts.slice(0, 20).map((a) => (
                <tr
                  key={a.alert_id}
                  className="border-t border-navy/[0.06]"
                  data-testid={`alert-row-${a.alert_id}`}
                >
                  <td className="py-2 font-mono text-[11px] text-ink-muted">
                    {a.created_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="py-2 text-xs">{a.startup_id}</td>
                  <td className="py-2 text-xs">
                    <Pill tone="muted">{a.alert_type}</Pill>
                  </td>
                  <td className="py-2 text-xs">{a.message}</td>
                  <td className="py-2 pr-2 text-right font-mono text-xs">
                    {(a.dispatched_to || []).length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card testId="nudge-history-card">
        <div className="overline mb-3">Nudge Runs — most recent</div>
        {nudgeHistory.length === 0 ? (
          <div className="text-ink-muted italic text-sm">No runs recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                <th className="text-left py-2">Completed</th>
                <th className="text-right py-2">Evaluated</th>
                <th className="text-right py-2">Nudged</th>
                <th className="text-right py-2">Below</th>
                <th className="text-right py-2 pr-2">Low events</th>
              </tr>
            </thead>
            <tbody>
              {nudgeHistory.map((r) => (
                <tr key={r.run_id} className="border-t border-navy/[0.06]">
                  <td className="py-2 font-mono text-[11px] text-ink-muted">
                    {r.completed_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="py-2 text-right font-mono">{r.evaluated}</td>
                  <td className="py-2 text-right font-mono text-gold">{r.nudged}</td>
                  <td className="py-2 text-right font-mono">{r.skipped_below_threshold}</td>
                  <td className="py-2 pr-2 text-right font-mono">{r.skipped_low_events}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card testId="digest-controls-card" className="mt-6">
        <div className="overline mb-3 flex items-center gap-2">
          <Mail size={14} /> Weekly Digest — bundles a full week of alerts
        </div>
        <p className="text-xs text-ink-muted mb-3">
          Every Sunday 07:00 UTC we email each investor a summary of alerts that fired on the
          startups they watch. Idempotent per (investor, week) — force to bypass, dry-run to inspect.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => runDigest(true, false)}
                   disabled={digestRunning} data-testid="digest-dry-run">
            {digestRunning ? <Loader2 className="animate-spin" size={14} /> : "Dry Run"}
          </Button>
          <Button size="sm" onClick={() => runDigest(false, false)}
                   disabled={digestRunning} data-testid="digest-run">
            {digestRunning ? <Loader2 className="animate-spin" size={14} /> : <><RefreshCw size={12} className="mr-1" /> Run Now</>}
          </Button>
          <Button variant="outline" size="sm" onClick={() => runDigest(false, true)}
                   disabled={digestRunning} data-testid="digest-force">
            Force (bypass week-lock)
          </Button>
        </div>
        {digestStats && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="digest-stats">
            <div className="border border-navy/[0.08] p-2 rounded">
              <div className="font-mono uppercase text-[10px] text-ink-muted">Evaluated</div>
              <div className="font-heading text-lg">{digestStats.evaluated}</div>
            </div>
            <div className="border border-navy/[0.08] p-2 rounded">
              <div className="font-mono uppercase text-[10px] text-ink-muted">Sent</div>
              <div className="font-heading text-lg text-gold">{digestStats.sent}</div>
            </div>
            <div className="border border-navy/[0.08] p-2 rounded">
              <div className="font-mono uppercase text-[10px] text-ink-muted">Empty week</div>
              <div className="font-heading text-lg">{digestStats.skipped_empty}</div>
            </div>
            <div className="border border-navy/[0.08] p-2 rounded">
              <div className="font-mono uppercase text-[10px] text-ink-muted">Already sent</div>
              <div className="font-heading text-lg">{digestStats.skipped_already_sent}</div>
            </div>
          </div>
        )}
        {digestHistory.length > 0 && (
          <div className="mt-4 border-t border-navy/[0.06] pt-3">
            <div className="overline mb-2">Recent Digests Sent</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                  <th className="text-left py-2">Sent</th>
                  <th className="text-left py-2">Investor</th>
                  <th className="text-right py-2">Alerts</th>
                  <th className="text-right py-2 pr-2">Startups</th>
                </tr>
              </thead>
              <tbody>
                {digestHistory.map((d) => (
                  <tr key={d.run_id} className="border-t border-navy/[0.06]">
                    <td className="py-2 font-mono text-[11px] text-ink-muted">
                      {d.sent_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="py-2 text-xs">{d.email}</td>
                    <td className="py-2 text-right font-mono">{d.total_alerts}</td>
                    <td className="py-2 pr-2 text-right font-mono">{d.startup_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </DashboardShell>
  );
}
